import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { projectsService } from '../services/projects.service';

/** Minimal shape of an accessible project (from projectsService.getAccessible). */
export interface ProjectLike {
  _id: string;
  name: string;
  appCode?: string;
}

/**
 * The projects this user can reach, for the Project column and filter every
 * scoped list carries.
 *
 * Shared through react-query so the seven lists that ask for it make one request
 * between them rather than one each, and a failure resolves to an empty list:
 * the Project column hides itself, which is a fair rendering of "we could not
 * find out" and never blocks the list it decorates.
 *
 * `canScopeByProject` is false for a user who reaches one project, because the
 * column would then repeat the same value on every row and the filter could only
 * ever be a no-op.
 */
export function useAccessibleProjects() {
  const { data: projects = [] } = useQuery({
    queryKey: ['projects', 'accessible'],
    queryFn: () => projectsService.getAccessible(),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return { projects, canScopeByProject: projects.length > 1 };
}

/**
 * The value standing for "no project — shared across all of them".
 *
 * Not the empty string, which is what a filter uses: PrimeReact's FloatLabel
 * reads an empty value as "nothing selected" and leaves the label sitting in the
 * field, where it collides with the option text the dropdown is already showing.
 * A real value keeps the field filled, so the label floats as it should.
 */
export const SHARED_PROJECT = '__shared__';

/**
 * Options for a project FIELD on a form.
 *
 * **Shared is not offered.** A row with no project is read by EVERY project, and
 * creating one by choosing an option in a dropdown is how a definition quietly
 * becomes everyone's. Whatever is authored here belongs to a project; the few
 * shared rows that exist were seeded deliberately and are still read.
 *
 * `SHARED_PROJECT` stays for those: a form opened on one shows it rather than an
 * empty field, and `appIdFromField` still maps it back.
 */
export const projectFieldOptions = (projects: ProjectLike[], currentValue?: string) => ([
  // Only ever for a row that IS shared already: a field whose value is missing
  // from its own options renders blank, which would read as "no project" on the
  // one kind of row where that is the whole meaning.
  ...(currentValue === SHARED_PROJECT
    ? [{ label: 'Shared (all projects)', value: SHARED_PROJECT }] : []),
  ...projects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id })),
]);

/**
 * Whether a row may be REFERENCED from something owned by `projectId`.
 *
 * The API's rule, in one place: a row reaches its own project's rows and the
 * shared ones, never another project's — `find_resolvable` returns nothing for a
 * cross-project reference and the save is refused as "does not exist in this
 * scope". A picker that offers what the save will reject turns a rule into a
 * puzzle, so the pickers filter by this instead of showing everything the
 * VIEWER can see.
 *
 * With no project decided yet, nothing is filtered — there is no rule to apply.
 */
export const referencableIn = (projectId?: string | null) =>
  (row: { appId?: string | null }): boolean =>
    !projectId || !row.appId || String(row.appId) === String(projectId);

/** What a NEW row defaults to: the project being worked in, never shared. */
export const defaultProjectId = (projects: ProjectLike[]): string =>
  localStorage.getItem('appId') || projects[0]?._id || '';

/** Turns what the field holds into what the API wants: undefined means shared. */
export const appIdFromField = (value?: string | null) =>
  (value && value !== SHARED_PROJECT ? value : undefined);

/** The option list a project filter or picker renders, label already formatted. */
export const projectOptions = (projects: ProjectLike[], allLabel = 'All Projects') => ([
  { label: allLabel, value: '' },
  ...projects.map((p) => ({ label: p.appCode ? `${p.name} (${p.appCode})` : p.name, value: p._id })),
]);

/** A project pill descriptor understood by FloatLabelDropdown option rendering. */
export interface ProjectTag {
  tag: string;
  tagTone: 'neutral' | 'accent';
}

/**
 * Shared helpers for project-scoped dependency dropdowns (see unit / department /
 * unit-type forms). Mirrors the backend "same project or global" FK rule:
 *
 *  - `belongsToProject(appId)` — an option is selectable when no project is chosen
 *    yet (show all), it is global (no appId), or it belongs to the selected project.
 *  - `projectTag(appId)` — inline pill: the project name (accent) or "Global" (grey).
 *
 * Only apply these to FKs the backend actually scope-checks; filtering a non-scoped
 * cascade field can hide valid global options.
 */
export function useProjectScope(
  selectedAppId: string | null | undefined,
  accessibleProjects: ProjectLike[]
) {
  const projectNameById = useMemo(() => {
    const map = new Map<string, string>();
    accessibleProjects.forEach((p) => map.set(p._id, p.name));
    return map;
  }, [accessibleProjects]);

  const belongsToProject = useCallback(
    (itemAppId?: string | null) => !selectedAppId || !itemAppId || itemAppId === selectedAppId,
    [selectedAppId]
  );

  const projectTag = useCallback(
    (itemAppId?: string | null): ProjectTag =>
      itemAppId
        ? { tag: projectNameById.get(itemAppId) || 'Project', tagTone: 'accent' }
        : { tag: 'Global', tagTone: 'neutral' },
    [projectNameById]
  );

  return { projectNameById, belongsToProject, projectTag };
}
